/**
 * Player de mídia sincronizado com transcrição.
 * 
 * Usa react-h5-audio-player para gerenciamento robusto do elemento <audio>,
 * garantindo detecção correta de duração, progresso e eventos de reprodução.
 * 
 * Exibe controles de reprodução completos (play/pause, seek, volume, timestamps)
 * e indicadores contextuais (falante ativo, erros, buffering).
 * 
 * Quando a mídia não está mais disponível (TTL expirado no S3), mostra aviso informativo.
 */

import React, { useEffect, useRef, useMemo, useState } from 'react'
import AudioPlayer from 'react-h5-audio-player'
import 'react-h5-audio-player/lib/styles.css'
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Alert,
  AlertTitle,
  CircularProgress,
  Paper
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import TimerOffIcon from '@mui/icons-material/TimerOff'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import CloseIcon from '@mui/icons-material/Close'
import SpeedIcon from '@mui/icons-material/Speed'
import type { UseMediaPlayerReturn } from '../hooks/useMediaPlayer'
import type { TranscriptSegment } from '../../../types/transcription'

type Props = {
  player: UseMediaPlayerReturn
  /** Segmentos da transcrição para indicar segmento ativo */
  segments?: TranscriptSegment[]
  /** Mapeamento de speakers para exibir nome do falante ativo */
  speakerMapping?: Record<string, string>
  /** Callback para fechar/ocultar o player */
  onClose?: () => void
}

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

export default function MediaPlayer({ player, segments, speakerMapping, onClose }: Props) {
  const {
    mediaAvailable,
    mediaUrl,
    isLoading,
    isPlaying,
    currentTime,
    message,
    error,
    mediaRef,
    refreshUrl,
    _onTimeUpdate,
    _onDurationChange,
    _onPlayStateChange,
    _onBufferingChange,
    _onError,
    _clearError
  } = player

  const playerRef = useRef<AudioPlayer>(null)
  const [playbackRate, setPlaybackRate] = useState(1)

  // Sincroniza o ref do <audio> interno do react-h5-audio-player com o hook
  // para que ações externas (seekTo, play/pause via clique no transcript) funcionem
  useEffect(() => {
    const audioEl = playerRef.current?.audio?.current
    if (audioEl && mediaRef) {
      (mediaRef as React.MutableRefObject<HTMLAudioElement | null>).current = audioEl
    }
  })

  // Aplica a velocidade de reprodução ao elemento <audio>
  useEffect(() => {
    const audio = playerRef.current?.audio?.current
    if (audio) audio.playbackRate = playbackRate
  }, [playbackRate])

  const handleSpeedCycle = () => {
    const idx = SPEED_OPTIONS.indexOf(playbackRate)
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length]
    setPlaybackRate(next)
  }

  // Falante ativo baseado no currentTime
  const activeSpeakerName = useMemo(() => {
    if (!segments || segments.length === 0 || !isPlaying) return null
    const seg = segments.find(s => {
      const start = typeof s.start_time === 'string' ? parseFloat(s.start_time) : s.start_time
      const end = typeof s.end_time === 'string' ? parseFloat(s.end_time) : s.end_time
      return currentTime >= start && currentTime < end
    })
    if (!seg) return null
    return speakerMapping?.[seg.speaker] || seg.speaker
  }, [segments, currentTime, isPlaying, speakerMapping])

  // --- Mídia indisponível ---
  if (!isLoading && !mediaAvailable) {
    return (
      <Alert
        severity="info"
        icon={<TimerOffIcon />}
        action={
          <Tooltip title="Verificar novamente">
            <IconButton size="small" onClick={refreshUrl}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        sx={{ mb: 2, borderRadius: 2 }}
      >
        <AlertTitle>Reprodução não disponível</AlertTitle>
        {message || 'O arquivo de mídia original foi removido após o período de retenção. A transcrição permanece disponível.'}
      </Alert>
    )
  }

  // --- Carregando ---
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Carregando player...
        </Typography>
      </Paper>
    )
  }

  // --- Player ativo ---
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: isPlaying ? 'primary.main' : 'grey.200',
        transition: 'border-color 0.3s ease',
        overflow: 'hidden',
        bgcolor: '#fff',

        // ── Overrides para o react-h5-audio-player ──
        '& .rhap_container': {
          boxShadow: 'none',
          background: 'transparent',
          padding: '4px 12px 8px',
        },
        '& .rhap_progress-section': {
          gap: '8px',
        },
        '& .rhap_progress-bar': {
          height: '4px',
          backgroundColor: '#e0e0e0',
        },
        '& .rhap_progress-filled': {
          backgroundColor: '#1976d2',
        },
        '& .rhap_progress-indicator': {
          width: '12px',
          height: '12px',
          top: '-4px',
          marginLeft: '-6px',
          backgroundColor: '#1976d2',
          boxShadow: 'none',
          transition: 'width 0.15s, height 0.15s',
        },
        '& .rhap_progress-bar:hover .rhap_progress-indicator, & .rhap_progress-indicator:focus': {
          width: '16px',
          height: '16px',
          top: '-6px',
          marginLeft: '-8px',
        },
        '& .rhap_download-progress': {
          backgroundColor: 'rgba(25, 118, 210, 0.12)',
        },
        '& .rhap_time': {
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          color: '#666',
        },
        '& .rhap_main-controls-button': {
          color: '#555',
          margin: '0 4px',
        },
        '& .rhap_play-pause-button': {
          fontSize: '36px',
          width: '36px',
          height: '36px',
        },
        '& .rhap_volume-button': {
          fontSize: '22px',
          color: '#777',
        },
        '& .rhap_volume-bar-area': {
          width: '70px',
        },
        '& .rhap_volume-bar': {
          height: '3px',
          backgroundColor: '#e0e0e0',
        },
        '& .rhap_volume-filled': {
          backgroundColor: '#888',
        },
        '& .rhap_volume-indicator': {
          width: '10px',
          height: '10px',
          backgroundColor: '#888',
          boxShadow: 'none',
        },
        '& .rhap_button-clear:hover': {
          opacity: 0.8,
        },
        '& .rhap_controls-section': {
          flex: 'unset',
        },
        '& .rhap_additional-controls': {
          flex: '0 0 auto',
        },
        '& .rhap_volume-controls': {
          flex: '0 0 auto',
        },
      }}
    >
      {/* Indicador de falante ativo + velocidade + botão fechar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 0.75, pb: 0 }}>
        {activeSpeakerName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GraphicEqIcon
              sx={{
                fontSize: 14,
                color: 'primary.main',
                animation: isPlaying ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                maxWidth: 250,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activeSpeakerName}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        <Box sx={{ flex: 1 }} />

        {/* Controle de velocidade */}
        <Tooltip title="Velocidade de reprodução">
          <Box
            onClick={handleSpeedCycle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.3,
              cursor: 'pointer',
              px: 0.8,
              py: 0.2,
              borderRadius: 1,
              bgcolor: playbackRate !== 1 ? 'primary.50' : 'transparent',
              border: '1px solid',
              borderColor: playbackRate !== 1 ? 'primary.200' : 'grey.300',
              '&:hover': { bgcolor: 'grey.100' },
              transition: 'all 0.2s',
              userSelect: 'none',
              mr: 0.5,
            }}
          >
            <SpeedIcon sx={{ fontSize: 14, color: playbackRate !== 1 ? 'primary.main' : 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                color: playbackRate !== 1 ? 'primary.main' : 'text.secondary',
                lineHeight: 1,
              }}
            >
              {playbackRate}x
            </Typography>
          </Box>
        </Tooltip>

        {onClose && (
          <Tooltip title="Fechar player">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: '#ffebee', color: '#d32f2f' },
                width: 28,
                height: 28,
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* react-h5-audio-player — gerencia o elemento <audio> e controles */}
      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        showJumpControls={false}
        showDownloadProgress={true}
        showFilledProgress={true}
        showFilledVolume={true}
        autoPlayAfterSrcChange={false}
        listenInterval={200}
        volume={0.8}
        layout="stacked"
        preload="auto"
        customAdditionalControls={[]}
        onListen={() => {
          const audio = playerRef.current?.audio?.current
          if (audio) _onTimeUpdate(audio.currentTime)
        }}
        onLoadedMetaData={() => {
          const audio = playerRef.current?.audio?.current
          if (audio) {
            if (audio.duration && isFinite(audio.duration)) {
              _onDurationChange(audio.duration)
            }
            audio.playbackRate = playbackRate
          }
          _clearError()
        }}
        onPlay={() => {
          _onPlayStateChange(true)
          _clearError()
        }}
        onPause={() => _onPlayStateChange(false)}
        onEnded={() => _onPlayStateChange(false)}
        onCanPlay={() => _onBufferingChange(false)}
        onLoadStart={() => _onBufferingChange(true)}
        onCanPlayThrough={() => _onBufferingChange(false)}
        onError={() => {
          _onError('Erro ao reproduzir mídia. O arquivo pode ter expirado.')
          _onPlayStateChange(false)
        }}
      />

      {/* Erro */}
      {error && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0, py: 0.5 }}
          action={
            <IconButton size="small" onClick={refreshUrl}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}
    </Box>
  )
}
